import {
  AdditiveBlending,
  CircleGeometry,
  Color,
  Mesh,
  MeshBasicMaterial,
  PointLight,
  Quaternion,
  SphereGeometry,
  Vector3,
  type Scene,
} from 'three';
import { clamp, lerp } from '../utils/rng';
import { terrainNormal } from '../world/heightfield';
import { scorchDecal, smokeSprite, softSprite } from '../world/textures';
import { ParticleField } from './ParticleField';

/**
 * Tout ce qui se passe au point d'impact : etincelles, fumee, boule de feu,
 * lumiere, et la trace laissee au sol.
 *
 * Les quatre effets ont des durees tres differentes -- 60 ms pour l'eclair,
 * huit secondes pour la fumee, indefiniment pour la trace -- et c'est pour ca
 * qu'ils sont separes. Un seul systeme devrait tenir la plus longue.
 */

/** Nombre de traces d'impact conservees au sol. */
const DECAL_POOL = 56;

/** Rayon d'une trace, en metres. */
const DECAL_RADIUS = 2.6;

const UP = new Vector3(0, 1, 0);

export class Impacts {
  private readonly sparks: ParticleField;
  private readonly smoke: ParticleField;
  private readonly debris: ParticleField;

  /** Les traces au sol, reutilisees en anneau. */
  private readonly decals: Mesh[] = [];
  private decalCursor = 0;

  /** Boule de feu : une sphere additive qui gonfle et s'eteint. */
  private readonly fireball: Mesh;
  private fireballTime = 0;
  private readonly fireballAt = new Vector3();

  private readonly light: PointLight;
  private lightTime = 0;

  private readonly sparkColor = new Color(0xffc266);
  private readonly hotColor = new Color(0xff7a2a);
  // Une fumee de poudre est grise et chaude, pas noire. Trop sombre, les
  // bouffees de bouche se lisent comme des taches opaques devant la lunette.
  private readonly smokeColor = new Color(0x70675a);
  private readonly debrisColor = new Color(0x6b5c43);

  private readonly tmpPos = new Vector3();
  private readonly tmpVel = new Vector3();
  private readonly tmpNormal = new Vector3();
  private readonly tmpQ = new Quaternion();

  constructor(scene: Scene) {
    this.sparks = new ParticleField(scene, {
      capacity: 1400,
      texture: softSprite(),
      blend: 'additive',
      gravity: -16,
      drag: 1.6,
      fadePower: 1.4,
    });

    this.debris = new ParticleField(scene, {
      capacity: 900,
      texture: softSprite(),
      blend: 'additive',
      // La terre retombe : elle subit la vraie gravite, pas celle des obus.
      gravity: -14,
      drag: 0.5,
      fadePower: 1.2,
    });

    this.smoke = new ParticleField(scene, {
      capacity: 900,
      texture: smokeSprite(),
      blend: 'soft',
      // La fumee chaude monte, puis le frottement la fige.
      gravity: 0.9,
      drag: 0.85,
      fadePower: 1.5,
    });

    this.fireball = new Mesh(
      new SphereGeometry(1, 12, 8),
      new MeshBasicMaterial({
        color: 0xffa24a,
        transparent: true,
        blending: AdditiveBlending,
        depthWrite: false,
      }),
    );
    this.fireball.visible = false;
    scene.add(this.fireball);

    this.light = new PointLight(0xffa24a, 0, 60, 2);
    scene.add(this.light);

    const geo = new CircleGeometry(DECAL_RADIUS, 16);
    const map = scorchDecal();
    for (let i = 0; i < DECAL_POOL; i++) {
      const decal = new Mesh(
        geo,
        new MeshBasicMaterial({
          map,
          transparent: true,
          // Sans depthWrite a false, la trace se dispute le z-buffer avec le
          // terrain et clignote. Le polygonOffset la pousse devant lui.
          depthWrite: false,
          polygonOffset: true,
          polygonOffsetFactor: -2,
          polygonOffsetUnits: -2,
          opacity: 0,
        }),
      );
      decal.visible = false;
      decal.renderOrder = 1;
      scene.add(decal);
      this.decals.push(decal);
    }
  }

  /**
   * Explosion au sol : terre projetee, etincelles, fumee, trace.
   *
   * `incoming` sert a incliner la gerbe dans le sens de l'obus : un coup
   * tendu projette la terre vers l'avant, un coup plongeant la projette
   * verticalement. C'est le detail qui fait que deux impacts ne se
   * ressemblent pas.
   */
  ground(point: Vector3, incoming: Vector3): void {
    terrainNormal(point.x, point.z, this.tmpNormal);
    // La gerbe part dans la direction de la reflexion de l'obus sur le sol.
    const bounce = this.tmpPos
      .copy(incoming)
      .reflect(this.tmpNormal)
      .normalize()
      .lerp(this.tmpNormal, 0.55)
      .normalize()
      .clone();

    this.burst(point, bounce, { sparks: 60, debris: 90, smoke: 26, power: 1 });
    this.flash(point, 1);
    this.layDecal(point);
  }

  /**
   * Coup au but : plus d'etincelles, moins de terre, et une boule de feu plus
   * franche. Pas de trace au sol -- la cible n'est pas le sol.
   */
  target(point: Vector3, incoming: Vector3): void {
    const bounce = this.tmpPos.copy(incoming).negate().normalize().clone();
    this.burst(point, bounce, { sparks: 130, debris: 40, smoke: 34, power: 1.5 });
    this.flash(point, 1.5);
  }

  /** Fumee de bouche : elle sort du frein de bouche, vers l'avant. */
  muzzleBlast(point: Vector3, direction: Vector3): void {
    for (let i = 0; i < 26; i++) {
      this.tmpPos.copy(point).addScaledVector(direction, Math.random() * 1.6);
      this.tmpVel
        .copy(direction)
        .multiplyScalar(5 + Math.random() * 9)
        .add(
          this.tmpNormal.set(
            (Math.random() - 0.5) * 4,
            (Math.random() - 0.5) * 3 + 0.8,
            (Math.random() - 0.5) * 4,
          ),
        );
      this.smoke.spawn(this.tmpPos, this.tmpVel, 0.8 + Math.random() * 1.1, this.smokeColor, 3.2);
    }
    for (let i = 0; i < 24; i++) {
      this.tmpPos.copy(point);
      this.tmpVel
        .copy(direction)
        .multiplyScalar(14 + Math.random() * 22)
        .add(
          this.tmpNormal.set(
            (Math.random() - 0.5) * 7,
            (Math.random() - 0.5) * 7,
            (Math.random() - 0.5) * 7,
          ),
        );
      this.sparks.spawn(this.tmpPos, this.tmpVel, 0.12 + Math.random() * 0.14, this.hotColor, 2.0);
    }
  }

  private burst(
    point: Vector3,
    axis: Vector3,
    counts: { sparks: number; debris: number; smoke: number; power: number },
  ): void {
    const p = counts.power;

    for (let i = 0; i < counts.sparks; i++) {
      this.tmpPos.copy(point);
      cone(this.tmpVel, axis, 0.9, 10 + Math.random() * 34 * p);
      this.sparks.spawn(
        this.tmpPos,
        this.tmpVel,
        0.18 + Math.random() * 0.45,
        Math.random() < 0.4 ? this.hotColor : this.sparkColor,
        1.3 + Math.random() * 1.1,
      );
    }

    for (let i = 0; i < counts.debris; i++) {
      this.tmpPos.copy(point);
      cone(this.tmpVel, axis, 0.65, 6 + Math.random() * 19 * p);
      this.debris.spawn(
        this.tmpPos,
        this.tmpVel,
        0.6 + Math.random() * 1.1,
        this.debrisColor,
        1.6 + Math.random() * 1.8,
      );
    }

    for (let i = 0; i < counts.smoke; i++) {
      this.tmpPos.copy(point).addScaledVector(axis, Math.random() * 1.2);
      cone(this.tmpVel, axis, 1.1, 1.5 + Math.random() * 5);
      this.smoke.spawn(
        this.tmpPos,
        this.tmpVel,
        1.6 + Math.random() * 2.6,
        this.smokeColor,
        3.4 + Math.random() * 2.6,
      );
    }
  }

  private flash(point: Vector3, power: number): void {
    this.fireballAt.copy(point);
    this.fireballTime = 0.18 * power;
    this.lightTime = 0.3 * power;
    this.light.position.copy(point);
    this.light.intensity = 1400 * power;
    this.fireball.position.copy(point);
    this.fireball.visible = true;
  }

  /**
   * Pose une trace au sol, orientee selon la normale du terrain.
   *
   * Un CircleGeometry est a plat dans le plan XY : il faut l'amener sur la
   * normale du sol, et le decoller de quelques centimetres pour qu'il ne
   * dispute pas le z-buffer au terrain.
   */
  private layDecal(point: Vector3): void {
    const decal = this.decals[this.decalCursor];
    this.decalCursor = (this.decalCursor + 1) % DECAL_POOL;
    if (!decal) return;

    terrainNormal(point.x, point.z, this.tmpNormal);
    decal.position.copy(point).addScaledVector(this.tmpNormal, 0.07);
    decal.quaternion.setFromUnitVectors(FORWARD, this.tmpNormal);
    // Une rotation au hasard autour de la normale : sinon les cinquante-six
    // traces du champ de tir sont la meme image, toutes dans le meme sens.
    decal.quaternion.multiply(this.tmpQ.setFromAxisAngle(FORWARD, Math.random() * Math.PI * 2));
    decal.scale.setScalar(0.75 + Math.random() * 0.5);
    (decal.material as MeshBasicMaterial).opacity = 0.9;
    decal.visible = true;
  }

  update(dt: number): void {
    this.sparks.update(dt);
    this.debris.update(dt);
    this.smoke.update(dt);

    if (this.fireballTime > 0) {
      this.fireballTime = Math.max(0, this.fireballTime - dt);
      const k = this.fireballTime / 0.18;
      // Elle gonfle en s'eteignant : la boule de feu d'une detonation se
      // dilate pendant qu'elle refroidit.
      this.fireball.scale.setScalar(lerp(3.4, 0.6, clamp(k, 0, 1)));
      const mat = this.fireball.material as MeshBasicMaterial;
      mat.opacity = clamp(k, 0, 1) * 0.85;
      this.fireball.position.copy(this.fireballAt);
      if (this.fireballTime === 0) this.fireball.visible = false;
    }

    if (this.lightTime > 0) {
      this.lightTime = Math.max(0, this.lightTime - dt);
      this.light.intensity *= Math.exp(-9 * dt);
      if (this.lightTime === 0) this.light.intensity = 0;
    }
  }

  reset(): void {
    this.sparks.reset();
    this.debris.reset();
    this.smoke.reset();
    this.fireballTime = 0;
    this.lightTime = 0;
    this.fireball.visible = false;
    this.light.intensity = 0;
    for (const d of this.decals) d.visible = false;
    this.decalCursor = 0;
  }

  dispose(): void {
    this.sparks.dispose();
    this.debris.dispose();
    this.smoke.dispose();
  }
}

/** Normale d'un CircleGeometry non tourne : il est dans le plan XY. */
const FORWARD = new Vector3(0, 0, 1);

/**
 * Tire une vitesse dans un cone autour de `axis`.
 *
 * `spread` est l'ouverture en radians. On construit une base orthonormee a
 * partir de l'axe plutot que de tirer dans une sphere et de rejeter : pas de
 * boucle de rejet, donc un cout constant par particule.
 */
function cone(out: Vector3, axis: Vector3, spread: number, speed: number): Vector3 {
  // Un vecteur non colineaire a l'axe, quel que soit l'axe.
  const helper = Math.abs(axis.y) < 0.9 ? UP : FORWARD;
  const t1 = TMP_A.copy(helper).cross(axis).normalize();
  const t2 = TMP_B.copy(axis).cross(t1).normalize();
  // cos uniforme sur la calotte : sinon les particules s'agglutinent sur l'axe.
  const cosMax = Math.cos(spread);
  const cosA = 1 - Math.random() * (1 - cosMax);
  const sinA = Math.sqrt(Math.max(0, 1 - cosA * cosA));
  const phi = Math.random() * Math.PI * 2;
  out
    .copy(axis)
    .multiplyScalar(cosA)
    .addScaledVector(t1, sinA * Math.cos(phi))
    .addScaledVector(t2, sinA * Math.sin(phi))
    .multiplyScalar(speed);
  return out;
}

const TMP_A = new Vector3();
const TMP_B = new Vector3();
